import React, { useEffect, useState } from 'react';
import { Spinner, useStateContext } from '@veda/dashboard-ui';
import { usePppApi } from '../services/pppApi';

function CanaleBadge({ canale }) {
  const map = {
    sito_istituzionale:          { cls: 'bg-blue-100 text-blue-700',     label: 'Sito Ist.' },
    amministrazione_trasparente: { cls: 'bg-purple-100 text-purple-700', label: 'Amm. Trasp.' },
    bdncp:                       { cls: 'bg-orange-100 text-orange-700', label: 'BDNCP' },
    guue:                        { cls: 'bg-red-100 text-red-700',       label: 'GUUE' },
    bdncp_guue:                  { cls: 'bg-red-100 text-red-700',       label: 'BDNCP+GUUE' },
  };
  const cfg = map[canale] ?? { cls: 'bg-gray-100 text-gray-600', label: canale };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>{cfg.label}</span>;
}

export default function ReportPubblicazioni() {
  const { _locate } = useStateContext();
  const api = usePppApi();
  const [procedimenti, setProcedimenti] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProc, setLoadingProc] = useState(true);

  useEffect(() => {
    api.listProcedimenti({
      onSuccess: (data) => { setProcedimenti(data.data ?? data); setLoadingProc(false); },
      onFormError: () => setLoadingProc(false),
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = () => {
    if (!selectedId) return;
    setLoading(true);
    setReport(null);
    api.getReportPubblicazioni(selectedId, {
      onSuccess: (data) => { setReport(data); setLoading(false); },
      onFormError: () => setLoading(false),
    });
  };

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {_locate('ppp_report_pubblicazioni_titolo')}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {_locate('ppp_report_pubblicazioni_desc')}
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            {_locate('ppp_report_seleziona_procedimento')}
          </label>
          {loadingProc ? <Spinner /> : (
            <select
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white"
              value={selectedId}
              onChange={(e) => { setSelectedId(e.target.value); setReport(null); }}
            >
              <option value="">{_locate('ppp_report_scegli_procedimento')}</option>
              {procedimenti.map((p) => (
                <option key={p.id} value={p.id}>{p.codice ? `${p.codice} — ` : ''}{p.titolo}</option>
              ))}
            </select>
          )}
        </div>
        <button
          onClick={carica}
          disabled={!selectedId || loading}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors"
        >
          {loading ? _locate('ppp_report_caricamento') : _locate('ppp_report_genera')}
        </button>
      </div>

      {loading && <div className="flex justify-center items-center h-40"><Spinner /></div>}

      {report && (
        <div className="space-y-6">
          {/* Sommario */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: _locate('ppp_report_pubb_totale'), value: report.totalePubblicazioni, color: 'blue' },
              { label: _locate('ppp_report_pubb_obblighi_assolti'), value: report.obblighiAssolti, color: 'green' },
              { label: _locate('ppp_report_pubb_obblighi_mancanti'), value: report.obblighiMancanti, color: report.obblighiMancanti > 0 ? 'red' : 'green' },
            ].map((kpi) => {
              const colors = {
                blue:  'bg-blue-50 dark:bg-blue-900/20 text-blue-700',
                green: 'bg-green-50 dark:bg-green-900/20 text-green-700',
                red:   'bg-red-50 dark:bg-red-900/20 text-red-700',
              };
              return (
                <div key={kpi.label} className={`rounded-xl p-4 text-center ${colors[kpi.color]}`}>
                  <div className="text-3xl font-bold">{kpi.value}</div>
                  <div className="text-xs mt-1 opacity-80">{kpi.label}</div>
                </div>
              );
            })}
          </div>

          {/* Tabella pubblicazioni */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-white">
                {report.codiceProcedimento} — {report.titoloProcedimento}
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_pubb_tipo')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_pubb_canale')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_pubb_titolo')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_pubb_data')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">{_locate('ppp_report_pubb_registrato_da')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">{_locate('ppp_report_pubb_url')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                  {report.pubblicazioni.map((p) => (
                    <tr key={p.logId} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                      <td className="px-4 py-3">
                        <div className="text-gray-900 dark:text-white">{p.tipoPubblicazioneLabel}</div>
                        {p.riferimentoNormativo && <div className="text-xs text-gray-400 italic mt-0.5">{p.riferimentoNormativo}</div>}
                      </td>
                      <td className="px-4 py-3"><CanaleBadge canale={p.canale} /></td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{p.titolo}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {new Date(p.dataPubblicazione).toLocaleDateString('it-IT')}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs hidden lg:table-cell">{p.registratoDa ?? '—'}</td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {p.urlPubblicazione ? (
                          <a href={p.urlPubblicazione} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs truncate max-w-[140px] block">
                            {p.urlPubblicazione}
                          </a>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
